import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout";
import api from "../../lib/api";
import {
  Lightbulb, Award, ChevronRight, RotateCcw, CheckCircle2, XCircle,
  Sparkles, Trophy,
} from "lucide-react";

/**
 * Module 3 — Scenario Decision-Tree Quiz.
 *
 * Loads a list of scenarios, then renders the chosen one as an interactive
 * branching tree. Each click submits the path so far; the final terminal
 * choice either awards XP (and a badge, on first correct completion) or
 * shows the explanation for the wrong path so the delegate learns.
 */
export default function ScenarioQuizPage() {
  const [scenarios, setScenarios] = useState([]);
  const [active, setActive] = useState(null);   // currently selected scenario
  const [path, setPath] = useState([]);          // chosen choice ids
  const [node, setNode] = useState(null);        // current node being shown
  const [result, setResult] = useState(null);    // terminal result after submit

  useEffect(() => {
    api.get("/lms/scenarios").then((r) => setScenarios(r.data.items || []));
  }, []);

  const start = async (s) => {
    const r = await api.get(`/lms/scenarios/${s.id}`);
    setActive(r.data); setPath([]); setNode(r.data.root); setResult(null);
  };

  const pick = async (choice) => {
    const newPath = [...path, choice.id];
    setPath(newPath);
    // If the choice has a `next`, walk deeper locally (no submit yet).
    if (choice.next) {
      const next = active.nodes.find((n) => n.id === choice.next);
      setNode(next);
      return;
    }
    // Terminal — submit and show result
    const r = await api.post("/lms/scenarios/submit", {
      scenario_id: active.id, chosen_path: newPath,
    });
    setResult(r.data);
  };

  const reset = () => { setActive(null); setPath([]); setNode(null); setResult(null); };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto" data-testid="scenario-quiz-root">
        <header className="mb-6">
          <p className="text-[10px] uppercase tracking-[0.36em] text-[#D4AF37] font-bold flex items-center gap-2">
            <Lightbulb className="h-3 w-3" /> Scenario Drills
          </p>
          <h1 className="mt-1 text-2xl font-bold text-[#0A192F]">
            Make the right call under pressure
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Branching decision trees rooted in MUN parliamentary procedure.
            Each scenario rewards XP and unlocks a badge on first perfect run.
          </p>
        </header>

        {!active && (
          <section className="grid sm:grid-cols-2 gap-4" data-testid="scenario-list">
            {scenarios.map((s) => (
              <button
                key={s.id} type="button" onClick={() => start(s)}
                className="text-left rounded-2xl border-2 border-slate-200 hover:border-[#D4AF37] bg-white p-5 transition shadow-sm hover:shadow-md"
                data-testid={`scenario-card-${s.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="text-[10px] uppercase tracking-wider text-[#D4AF37] font-bold">
                    {s.difficulty}
                  </p>
                  {s.completed && (
                    <span className="text-[10px] uppercase tracking-wider bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-bold flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3" /> Done
                    </span>
                  )}
                </div>
                <h3 className="mt-2 text-base font-bold text-[#0A192F]">{s.title}</h3>
                <p className="mt-1 text-xs text-slate-500 line-clamp-3">{s.summary}</p>
                <p className="mt-3 text-xs text-[#0F4C81] font-semibold flex items-center gap-1">
                  <Sparkles className="h-3 w-3" /> {s.xp_reward} XP
                  {s.badge_id && <> · <Trophy className="h-3 w-3" /> Badge</>}
                </p>
              </button>
            ))}
            {scenarios.length === 0 && (
              <p className="col-span-2 text-sm text-slate-400">
                No scenarios available yet.
              </p>
            )}
          </section>
        )}

        {active && !result && node && (
          <ScenarioInProgress
            scenario={active} node={node} pathLen={path.length}
            onPick={pick} onReset={reset}
          />
        )}

        {active && result && (
          <ScenarioResult
            scenario={active} result={result} onReset={reset} onRetry={() => start(active)}
          />
        )}
      </div>
    </DashboardLayout>
  );
}

function ScenarioInProgress({ scenario, node, pathLen, onPick, onReset }) {
  return (
    <section className="rounded-2xl border-2 border-[#0F4C81] bg-white p-6" data-testid="scenario-active">
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-[0.22em] text-[#0F4C81] font-bold">
          Step {pathLen + 1} · {scenario.title}
        </p>
        <button type="button" onClick={onReset}
                className="text-xs text-slate-500 hover:text-[#0A192F] flex items-center gap-1"
                data-testid="scenario-reset-btn">
          <RotateCcw className="h-3 w-3" /> Reset
        </button>
      </div>
      <p className="mt-3 text-lg font-bold text-[#0A192F]" data-testid="scenario-prompt">
        {node.prompt}
      </p>
      <div className="mt-5 space-y-2">
        {node.choices.map((c) => (
          <button
            key={c.id} type="button" onClick={() => onPick(c)}
            className="w-full text-left px-4 py-3 rounded-xl border border-slate-200 hover:border-[#D4AF37] hover:bg-[#FFFBEB] transition flex items-center justify-between gap-3"
            data-testid={`scenario-choice-${c.id}`}
          >
            <span className="text-sm text-[#0A192F]">{c.label}</span>
            <ChevronRight className="h-4 w-4 text-slate-400" />
          </button>
        ))}
      </div>
    </section>
  );
}

function ScenarioResult({ scenario, result, onReset, onRetry }) {
  const correct = result.outcome === "correct";
  return (
    <section
      className={`rounded-2xl border-2 ${correct ? "border-emerald-400 bg-emerald-50" : "border-rose-300 bg-rose-50"} p-6`}
      data-testid="scenario-result"
    >
      <div className="flex items-center gap-2">
        {correct ? <CheckCircle2 className="h-6 w-6 text-emerald-600" /> :
                    <XCircle className="h-6 w-6 text-rose-600" />}
        <h2 className="text-xl font-bold text-[#0A192F]" data-testid="scenario-result-headline">
          {correct ? "Correct!" : "Not quite."}
        </h2>
      </div>
      <p className="mt-3 text-sm text-[#0A192F]">{result.feedback}</p>
      {result.explanation && (
        <p className="mt-2 text-xs text-slate-700 italic">
          Why: {result.explanation}
        </p>
      )}
      {correct && result.xp_awarded > 0 && (
        <p className="mt-3 text-sm text-[#0F4C81] font-semibold flex items-center gap-1"
           data-testid="scenario-xp">
          <Sparkles className="h-4 w-4" /> +{result.xp_awarded} XP awarded
          {scenario.badge_id && <> · Badge unlocked</>}
        </p>
      )}
      {correct && result.xp_awarded === 0 && (
        <p className="mt-3 text-xs text-slate-500" data-testid="scenario-already-earned">
          You&apos;ve already perfected this scenario — XP not double-credited.
        </p>
      )}

      <div className="mt-5 flex gap-2">
        <button type="button" onClick={onRetry}
                className="px-4 py-2 rounded-md bg-[#0A192F] text-white text-sm font-semibold"
                data-testid="scenario-retry-btn">
          Try again
        </button>
        <button type="button" onClick={onReset}
                className="px-4 py-2 rounded-md bg-slate-100 hover:bg-slate-200 text-[#0A192F] text-sm font-semibold"
                data-testid="scenario-back-btn">
          Pick another
        </button>
      </div>
    </section>
  );
}
